"use client"

import type { MembersBoardProfiles } from "@ibada/shared-types/overview"

import { HomeTile, HomeTileEmpty, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { useTranslations } from "@/i18n"

enum MissingField {
  Phone = "phone",
  BirthDate = "birthDate",
  Gender = "gender",
}

/**
 * Profiles: how complete member records are (visitors left out), and which field is missing most, so the next
 * data-entry pass knows where to start. Each gap shows its count, never a bare percentage.
 */
export function ProfilesTile({ profiles, membersLink }: { profiles: MembersBoardProfiles; membersLink: HomeTileLink | null }) {
  const t = useTranslations("people.members.overview.board.profiles")
  const completeShare = profiles.total > 0 ? Math.round((profiles.complete / profiles.total) * 100) : 0
  const gaps = [
    { key: MissingField.Phone, label: t("missing.phone"), value: profiles.missingPhone },
    { key: MissingField.BirthDate, label: t("missing.birthDate"), value: profiles.missingBirthDate },
    { key: MissingField.Gender, label: t("missing.gender"), value: profiles.missingGender },
  ]
  const worst = [...gaps].sort((left, right) => right.value - left.value)[0]

  return (
    <HomeTile title={t("title")} subtitle={t("subtitle")} link={membersLink} testId='members-board-profiles'>
      {profiles.total === 0 ? (
        <HomeTileEmpty message={t("empty")} />
      ) : (
        <>
          <div className='flex items-baseline gap-2'>
            <span className='text-[1.875rem] leading-none font-extrabold tracking-tight tabular-nums'>{completeShare}%</span>
            <span className='text-[12.5px] text-muted-foreground'>
              {t("complete", { complete: profiles.complete, total: profiles.total })}
            </span>
          </div>
          <dl className='mt-1 flex flex-col gap-2'>
            {gaps.map((gap) => (
              <div key={gap.key} data-testid='profile-gap' className='flex flex-col gap-1'>
                <span className='flex text-[12.5px]'>
                  <dt className='text-muted-foreground'>{gap.label}</dt>
                  <dd className='ml-auto font-bold tabular-nums'>{gap.value}</dd>
                </span>
                <span className='flex h-1.5 overflow-hidden rounded-full bg-muted' aria-hidden>
                  {gap.value > 0 ? (
                    <span
                      className='rounded-full bg-amber-500 dark:bg-amber-400'
                      style={{ width: `${(gap.value / profiles.total) * 100}%` }}
                    />
                  ) : null}
                </span>
              </div>
            ))}
          </dl>
          <span className='mt-auto text-[11.5px] text-muted-foreground'>
            {worst && worst.value > 0 ? t("startWith", { field: worst.label.toLocaleLowerCase() }) : t("allComplete")}
          </span>
        </>
      )}
    </HomeTile>
  )
}
